import React, { useState } from 'react';
import { Play, Image, Video } from 'lucide-react';
import shaktiLearnEpisode from './images/Shakti Learn Episode.mp4';
import birondi from './images/Birondi.mp4';
import video2 from './images/Video2.mp4';
import shaktiLearnVideo3 from './images/ShaktiLearn Video 3.mp4';
import shaktiLearnBraveWomen from './images/ShaktiLearnBraveWomen.jpeg';
import empoweringThroughEducation from './images/Empowering Through Education.jpeg';
import birondiThumbnail from './images/Birondi.jpeg';
import interactiveLearning from './images/Interactive Learning.jpeg';
import celebratingStudentSuccess from './images/Celebrating Student Success.jpeg';
import shaktiLearnLicense from './images/ShakitlearnLicense.jpeg';

export default function GallerySection() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Gallery data with actual Shakti Learn media
  const galleryItems = [
    {
      id: 1,
      type: 'video',
      src: shaktiLearnEpisode,
      thumbnail: empoweringThroughEducation,
      category: 'learning',
      title: 'Shakti Learn: Empowering Through Education',
      description: 'Watch our comprehensive overview of how Shakti Learn is transforming lives through personalized English education and digital skills training for young Indian women.'
    },
    {
      id: 2,
      type: 'video',
      src: birondi,
      thumbnail: birondiThumbnail,
      category: 'community',
      title: 'Birondi: Stories of Resilience',
      description: 'Experience the inspiring journey of Birondi, showcasing the determination and strength of our students as they overcome challenges to pursue education.'
    },
    {
      id: 3,
      type: 'video',
      src: video2,
      thumbnail: interactiveLearning,
      category: 'learning',
      title: 'Interactive Learning in Action',
      description: 'From long journeys on unpaved roads to classrooms filled with hope, see how interactive learning reaches even the most remote corners.'
    },
    {
      id: 4,
      type: 'video',
      src: shaktiLearnVideo3,
      thumbnail: celebratingStudentSuccess,
      category: 'achievements',
      title: 'A School Transformed: Learning with New Possibilities',
      description: 'A government school renewed with modern classrooms, large-screen TVs, and new opportunities for every student.'
    },
    {
      id: 5,
      type: 'image',
      src: shaktiLearnBraveWomen,
      alt: 'Brave women who are breadwinners for their families',
      category: 'community',
      title: 'Brave Women: Balancing Work and Education',
      description: 'These brave women are the breadwinners for their families, but that doesn\'t mean they have to give up on their education. We worked hard to explain that they could still learn during their free time, and we managed to bring Seema (the gorgeous girl in the yellow suit) into the Shakti Learn fold.'
    },
    {
      id: 6,
      type: 'image',
      src: shaktiLearnLicense,
      alt: 'Shakti Learn License from Registrar in Greater Noida',
      category: 'achievements',
      title: 'Government Registration Achieved',
      description: 'Happy to get the license for Shakti Learn from the registrar in Greater Noida.'
    }
  ];

  const filters = [
    { key: 'all', label: 'All Media' },
    { key: 'learning', label: 'Learning' },
    { key: 'events', label: 'Events' },
    { key: 'digital-skills', label: 'Digital Skills' },
    { key: 'achievements', label: 'Achievements' },
    { key: 'community', label: 'Community' }
  ];

  const filteredItems = activeFilter === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeFilter);

  const openModal = (item) => {
    setSelectedMedia(item);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  // Handle escape key press
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (selectedMedia) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [selectedMedia]);

  return (
    <div className="min-h-screen bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Gallery</h1>
          <p className="text-xl text-gray-600">
            Explore our journey through photos and videos
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-6 py-2 rounded-full font-medium transition-colors duration-200 ${
                activeFilter === filter.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="group relative bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
              onClick={() => openModal(item)}
            >
              <div className="relative aspect-square">
                <img
                  src={item.type === 'video' ? item.thumbnail : item.src}
                  alt={item.alt || item.title}
                  className="w-full h-full object-cover"
                />
                {item.type === 'video' && (
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                    <div className="bg-white bg-opacity-90 rounded-full p-3">
                      <Play className="h-6 w-6 text-purple-600 ml-1" />
                    </div>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  {item.type === 'video' ? (
                    <Video className="h-5 w-5 text-white bg-black bg-opacity-50 rounded p-1" />
                  ) : (
                    <Image className="h-5 w-5 text-white bg-black bg-opacity-50 rounded p-1" />
                  )}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                  {filters.find(f => f.key === item.category)?.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Modal for viewing media */}
        {selectedMedia && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <div 
              className="relative max-w-4xl max-h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-white text-2xl font-bold z-10 hover:text-gray-300 bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
              <div className="bg-white rounded-lg overflow-hidden">
                {selectedMedia.type === 'video' ? (
                  <video
                    controls
                    className="w-full max-h-[60vh] object-contain"
                    autoPlay
                  >
                    <source src={selectedMedia.src} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    src={selectedMedia.src}
                    alt={selectedMedia.alt || selectedMedia.title}
                    className="w-full max-h-[60vh] object-contain"
                  />
                )}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedMedia.title}</h3>
                  <span className="inline-block px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 rounded-full mb-3">
                    {filters.find(f => f.key === selectedMedia.category)?.label}
                  </span>
                  {selectedMedia.description && (
                    <p className="text-gray-600 leading-relaxed text-base">{selectedMedia.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-purple-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Share Your Moments</h2>
            <p className="text-gray-600 mb-6">
              Have photos or videos from our programs? We'd love to see them!
            </p>
            <button className="bg-purple-600 text-white px-8 py-3 rounded-md font-medium hover:bg-purple-700 transition-colors duration-200">
              Submit Media
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 